///
/// EarthBrowser
///
/// Created by Matt Giger on 11/2/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Forecast radar overlay: the RRFS composite reflectivity drawn on the
/// radar palette past the end of the observed mosaic, over a hatch of the
/// REFS probability of > 40 dBZ echoes (overlay_radar_forecast.glsl).
///
/// Two texture series share one material: the 15-minute reflectivity
/// frames, flow-advected between steps like the mosaic, and the hourly
/// probability frames, cross-faded. Both are regional CONUS frames placed
/// by their bounds (the shader remaps globe UV into the frame like the
/// original MRMS overlay did).
///
/// The forecast only shows once the clock has moved past the newest
/// observation: `weight` ramps 0 -> 1 over the ten minutes after
/// observedEndMs + a live-lag allowance, and the observed mosaic and
/// its refinement tiles yield by the same weight (RadarOverlay) so the
/// hand-off is one dissolve rather than two fields drawn on top of each
/// other.
///

import { TextureOverlay } from "@/earthbrowser/datasets/TextureOverlay"
import { type LayerLoadState } from "@/earthbrowser/datasets/LoadState"
import { precipAltitudeScale } from "@/earthbrowser/datasets/precipAltitude"
import { type TimeLaneDetail } from "@/earthbrowser/datasets/TimeLane"
import { PrecipFlowTextureSeries } from "@/earthbrowser/dataset-distribution/imerg/PrecipFlowTextureSeries"
import { RadarTextureSeries } from "@/earthbrowser/dataset-distribution/mrms/MrmsOverlay"
import { type RadarTextureFrame } from "@/earthbrowser/dataset-distribution/mrms/RadarTextureFrame"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { wgs } from "@/earthbrowser/utils/geographic"
import { type RenderState } from "@stovokor/engine/RenderState"
import { clamp } from "@stovokor/math/math"
import { type timerange } from "@stovokor/math/time"
import { type Material } from "@stovokor/shader/Material"
import { type Texture } from "@stovokor/shader/Texture"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { vec4 } from "@stovokor/math/vector"

// The observed mosaic holds its newest frame while live "now" runs past
// it (mosaic build + publish + client poll, 6-13 minutes observed), so
// the forecast waits this long after the last observation before fading
// in: live mode keeps showing the observation...
const cHandoffLagMs = 15 * 60_000
// ...and takes this long to fade in fully (observed echoes fade out over
// the same span)
const cHandoffSpanMs = 10 * 60_000

/** [minLon, minLat, maxLon, maxLat] -> [minU, minV, maxU, maxV] in globe texture coordinates */
export function boundsToTexCoords(bounds: number[]): vec4 {
	const [west, south, east, north] = bounds
	return new vec4((west + 180) / 360, (90 - north) / 180, (east + 180) / 360, (90 - south) / 180)
}

export class RadarForecastOverlay extends TextureOverlay {
	private ebContext: EarthBrowserContext
	private flowSeries: PrecipFlowTextureSeries
	private probSeries: RadarTextureSeries<RadarTextureFrame>
	/** Frame bounds in globe texture coordinates; empty until frames arrive */
	private reflBounds = new vec4(0, 0, 0, 0)
	private probBounds = new vec4(0, 0, 0, 0)
	/** Time (ms) of the newest observed radar frame; undefined = none loaded */
	public observedEndMs: () => number | undefined = () => undefined
	/** 0 while the clock is within the observed radar, 1 once fully in the forecast */
	public weight = 0

	constructor(context: EarthBrowserContext) {
		super(context)
		this.ebContext = context
		this.flowSeries = new PrecipFlowTextureSeries(context)
		this.flowSeries.packPairs = true
		this.flowSeries.debugLabel = "radar-forecast:refc"
		this.textureSeries = this.flowSeries
		this.probSeries = new RadarTextureSeries<RadarTextureFrame>(context)
		this.probSeries.packPairs = true
		this.probSeries.debugLabel = "radar-forecast:prob40"
	}

	/** Reflectivity frames (chronological) and the regional extent they share */
	public setReflectivityFrames(frames: RadarTextureFrame[], bounds: number[] | null): void {
		this.flowSeries.setFrames(frames)
		if (bounds) this.reflBounds = boundsToTexCoords(bounds)
	}

	/** Probability frames (chronological) and their extent */
	public setProbabilityFrames(frames: RadarTextureFrame[], bounds: number[] | null): void {
		this.probSeries.setFrames(frames)
		if (bounds) this.probBounds = boundsToTexCoords(bounds)
	}

	public override getTimeRange(): timerange | undefined {
		return this.flowSeries.timeRange ?? this.probSeries.timeRange
	}

	public override getTimeLaneDetail(): TimeLaneDetail {
		return {
			frameTimesMs: this.flowSeries.frameTimesMs(),
			loadedRanges: this.flowSeries.loadedRangesMs(),
			displayedFrameMs: this.flowSeries.displayedFrameTimeMs(),
		}
	}

	public override getLoadState(): LayerLoadState | null {
		// Only report while the forecast is actually what's on screen
		return this.weight > 0 ? this.flowSeries.loadState() : null
	}

	public override purge(): void {
		this.flowSeries.purgeAll()
		this.probSeries.purgeAll()
	}

	public overlayMaterial(): Material {
		return new RadarForecastOverlayMaterial(this.ebContext, this)
	}

	public get reflectivitySeries(): PrecipFlowTextureSeries {
		return this.flowSeries
	}

	public get probabilitySeries(): RadarTextureSeries<RadarTextureFrame> {
		return this.probSeries
	}

	public get reflectivityBounds(): vec4 {
		return this.reflBounds
	}

	public get probabilityBounds(): vec4 {
		return this.probBounds
	}

	public override frameUpdate(state: RenderState): void {
		this.flowSeries.updateState(state)
		this.probSeries.updateState(state)

		const clockMs = this.ebContext.clock.clockTime.date.getTime()
		const cutoff = (this.observedEndMs() ?? Date.now()) + cHandoffLagMs
		const t = clamp((clockMs - cutoff) / cHandoffSpanMs, 0, 1)
		this.weight = t * t * (3 - 2 * t)
	}
}

class RadarForecastOverlayMaterial implements Material {
	public shaderDefines: Record<string, string> = {
		"RADAR_FORECAST_TEXTURES": "1"
	}
	public available: boolean = true
	public transparency: boolean = false
	public radarFcstPair = new UniformDynamic<Texture>()
	public radarFcstFlow = new UniformDynamic<Texture>()
	public radarFcstProbPair = new UniformDynamic<Texture>()
	public radarFcstFade = new UniformDynamic<number>()
	public radarFcstBlend = new UniformDynamic<number>()
	public radarFcstProbFade = new UniformDynamic<number>()
	public radarFcstProbBlend = new UniformDynamic<number>()
	public radarFcstBounds = new UniformDynamic<vec4>()
	public radarFcstProbBounds = new UniformDynamic<vec4>()
	public radarFcstAltitudeScale = new UniformDynamic<number>()

	private overlay: RadarForecastOverlay

	constructor(_context: EarthBrowserContext, overlay: RadarForecastOverlay) {
		this.overlay = overlay
	}

	public renderUpdate(state: RenderState): void {
		const refl = this.overlay.reflectivitySeries
		const prob = this.overlay.probabilitySeries
		const weight = this.overlay.weight
		this.radarFcstPair.value = refl.pairTexture
		this.radarFcstFlow.value = refl.flowTexture
		this.radarFcstFade.value = refl.fadeFactor * weight
		this.radarFcstBlend.value = refl.blendFactor
		this.radarFcstProbPair.value = prob.pairTexture
		this.radarFcstProbFade.value = prob.fadeFactor * weight
		this.radarFcstProbBlend.value = prob.blendFactor
		this.radarFcstBounds.value = this.overlay.reflectivityBounds
		this.radarFcstProbBounds.value = this.overlay.probabilityBounds
		this.radarFcstAltitudeScale.value = precipAltitudeScale(state.camera.position.length - wgs.a)
	}
}
